import React from 'react';
import { Text, Icon } from '../ui/index.jsx';

/**
 * Progress step component
 */
function ProgressStep({ label, icon, status, subLabel }) {
  const getStatusStyles = () => {
    switch (status) {
      case 'completed':
        return {
          bg: 'bg-green-500',
          border: 'border-green-500',
          text: 'text-green-400',
          icon: 'check'
        };
      case 'active':
        return {
          bg: 'bg-[#7B61FF]',
          border: 'border-[#7B61FF]',
          text: 'text-[#BA87FF]',
          icon: icon
        };
      case 'pending':
      default:
        return {
          bg: 'bg-[#2A2A3C]',
          border: 'border-[#2A2A3C]',
          text: 'text-gray-500',
          icon: icon
        };
    }
  };

  const styles = getStatusStyles();

  return (
    <div className="flex flex-col items-center">
      <div
        className={`w-10 h-10 rounded-full ${styles.bg} border-2 ${styles.border} flex items-center justify-center transition-all duration-300 ${status === 'active' ? 'ring-4 ring-[#7B61FF]/20' : ''}`}
      >
        {status === 'active' ? (
          <div className="animate-spin">
            <Icon name="loader" size="sm" className="text-white" />
          </div>
        ) : (
          <Icon name={styles.icon} size="sm" className="text-white" />
        )}
      </div>
      <Text className={`mt-2 text-sm font-medium ${styles.text}`}>
        {label}
      </Text>
      {subLabel && status === 'active' && (
        <Text className="text-xs text-gray-500 mt-0.5">
          {subLabel}
        </Text>
      )}
    </div>
  );
}

/**
 * Connector line between steps
 */
function ProgressConnector({ status }) {
  return (
    <div className="flex-1 h-0.5 mx-2 mt-5">
      <div
        className={`h-full transition-all duration-500 ${
          status === 'completed'
            ? 'bg-green-500'
            : status === 'active'
            ? 'bg-gradient-to-r from-green-500 to-[#7B61FF]'
            : 'bg-[#2A2A3C]'
        }`}
      />
    </div>
  );
}

/**
 * DeployProgress component - Visual progress bar for deployment phases
 * Enhanced with streaming progress (file-by-file generation)
 */
export default function DeployProgress({
  phase,
  generationPhase,
  filesCount = 0,
  // 🌊 NEW: Streaming progress props
  currentFileName = null,
  currentFileIndex = 0,
  totalFilesInPhase = 0,
  currentFileList = []
}) {
  // Define steps and their status
  const getStepStatus = (stepPhases) => {
    if (stepPhases.includes(phase)) return 'active';

    const phaseOrder = ['idle', 'generating', 'building', 'preview_ready', 'deploying', 'completed'];
    const currentIndex = phaseOrder.indexOf(phase);
    const stepIndex = Math.min(...stepPhases.map(p => phaseOrder.indexOf(p)));

    if (currentIndex > stepIndex) return 'completed';
    return 'pending';
  };

  // Get sub-label for generation phase (enhanced with current file)
  const getGenerationSubLabel = () => {
    if (phase !== 'generating') return null;

    // Show current file if available
    if (currentFileName) {
      const shortName = currentFileName.split('/').pop();
      return `${shortName} (${currentFileIndex}/${totalFilesInPhase || filesCount})`;
    }

    const labels = {
      foundation: 'Setting up project...',
      core: 'Creating components...',
      styling: 'Adding styles...',
      integration: 'Connecting APIs...'
    };

    return labels[generationPhase] || `${filesCount} files generated`;
  };

  const steps = [
    {
      label: 'Generate',
      icon: 'code',
      phases: ['generating'],
      subLabel: getGenerationSubLabel()
    },
    {
      label: 'Build',
      icon: 'package',
      phases: ['building'],
      subLabel: phase === 'building' ? 'Installing dependencies...' : null
    },
    {
      label: 'Preview',
      icon: 'eye',
      phases: ['preview_ready'],
      subLabel: phase === 'preview_ready' ? 'Ready for review' : null
    },
    {
      label: 'Deploy',
      icon: 'rocket',
      phases: ['deploying', 'completed'],
      subLabel: phase === 'deploying' ? 'Publishing...' : phase === 'completed' ? 'Live!' : null
    }
  ];

  return (
    <div className="bg-[#1A1A24] border border-[#2A2A3C] rounded-xl p-6">
      <div className="flex items-start justify-between">
        {steps.map((step, index) => (
          <React.Fragment key={step.label}>
            <ProgressStep
              label={step.label}
              icon={step.icon}
              status={getStepStatus(step.phases)}
              subLabel={step.subLabel}
            />
            {index < steps.length - 1 && (
              <ProgressConnector
                status={
                  getStepStatus(step.phases) === 'completed'
                    ? 'completed'
                    : getStepStatus(step.phases) === 'active'
                    ? 'active'
                    : 'pending'
                }
              />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Generation sub-progress */}
      {phase === 'generating' && (
        <div className="mt-6 pt-4 border-t border-[#2A2A3C]">
          <div className="flex items-center justify-between mb-2">
            <Text className="text-xs text-gray-400">Generation Phase</Text>
            <Text className="text-xs text-[#BA87FF]">{filesCount} files</Text>
          </div>
          <div className="flex gap-1">
            {['foundation', 'core', 'styling', 'integration'].map((gPhase) => {
              const isComplete = ['foundation', 'core', 'styling', 'integration']
                .indexOf(generationPhase) > ['foundation', 'core', 'styling', 'integration'].indexOf(gPhase);
              const isActive = generationPhase === gPhase;

              return (
                <div
                  key={gPhase}
                  className={`flex-1 h-1.5 rounded-full transition-all duration-300 ${
                    isComplete
                      ? 'bg-green-500'
                      : isActive
                      ? 'bg-[#7B61FF] animate-pulse'
                      : 'bg-[#2A2A3C]'
                  }`}
                />
              );
            })}
          </div>
          <div className="flex justify-between mt-1">
            <Text className="text-xs text-gray-600">Foundation</Text>
            <Text className="text-xs text-gray-600">Core</Text>
            <Text className="text-xs text-gray-600">Styling</Text>
            <Text className="text-xs text-gray-600">Integration</Text>
          </div>

          {/* 🌊 NEW: Current file indicator */}
          {currentFileName && (
            <div className="mt-4 pt-3 border-t border-[#2A2A3C]/50">
              <div className="flex items-center gap-2">
                <div className="animate-pulse">
                  <Icon name="file-code" size="xs" className="text-[#7B61FF]" />
                </div>
                <Text className="text-xs text-gray-300 font-mono truncate flex-1">
                  {currentFileName}
                </Text>
                <Text className="text-xs text-[#BA87FF] whitespace-nowrap">
                  {currentFileIndex}/{totalFilesInPhase}
                </Text>
              </div>
              {/* File progress bar */}
              {totalFilesInPhase > 0 && (
                <div className="mt-2 h-1 bg-[#2A2A3C] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#7B61FF] to-[#BA87FF] transition-all duration-300"
                    style={{ width: `${(currentFileIndex / totalFilesInPhase) * 100}%` }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
